(() => {
  const SEARCH_PATH = '/search';
  const GENERATED_FILTER = 'data-jb-precise-search-filter';

  const stripQuotes = (value) => {
    const trimmed = value.trim();
    if (trimmed.length >= 2 && trimmed.startsWith('"') && trimmed.endsWith('"')) {
      return trimmed.slice(1, -1).trim();
    }
    return trimmed;
  };

  const normalizedNumber = (value) => {
    const number = Number.parseFloat(String(value).replace(',', '.'));
    if (!Number.isFinite(number)) return String(value);
    return Number.isInteger(number) ? String(number) : String(number).replace(/0+$/, '').replace(/\.$/, '');
  };

  const buildPreciseSearch = (value) => {
    const raw = stripQuotes(value);
    let compact = raw.replace(/\s+/g, '');
    // Accept common customer notation without guessing a different tire size.
    const metricAlias = compact.match(/^(LT|P|ST)?(\d{3})(?:[\/-]?)(\d{2})(?:[\/-]|ZR?|R)?(\d{2})(?:LT)?$/i);
    if (metricAlias) compact = (metricAlias[1] || '') + metricAlias[2] + '/' + metricAlias[3] + 'R' + metricAlias[4];
    const flotationAlias = compact.match(/^(?:LT)?(\d{2})(\d{2})(\d{2})(\d{2})(?:LT)?$/i);
    if (flotationAlias) compact = flotationAlias[1] + 'x' + flotationAlias[2] + '.' + flotationAlias[3] + 'R' + flotationAlias[4];
    let match;

    match = compact.match(/^(\d{2})[xX×](\d{1,2}(?:[.,]\d+)?)$/);
    if (match) {
      const diameter = Number.parseInt(match[1], 10);
      const width = Number.parseFloat(match[2].replace(',', '.'));
      if (diameter >= 12 && diameter <= 30 && width >= 4 && width <= 20) {
        const canonical = `${diameter}x${normalizedNumber(match[2])}`;
        return {
          query: canonical,
          filters: {
            'filter.p.m.custom.diametre_roue': String(diameter),
            'filter.p.m.custom.largeur_roue': normalizedNumber(match[2]),
          },
        };
      }
    }

    match = compact.match(/^(\d{1,2})[xX×](\d{2,3}(?:[.,]\d+)?)$/);
    if (match) {
      const lugs = Number.parseInt(match[1], 10);
      const pcd = Number.parseFloat(match[2].replace(',', '.'));
      if (lugs >= 3 && lugs <= 10 && pcd >= 70 && pcd <= 300) {
        const commonShorthand = {
          '5x114': '5x114.3',
          '5x139': '5x139.7',
          '6x139': '6x139.7',
          '8x165': '8x165.1',
        };
        const entered = `${lugs}x${normalizedNumber(match[2])}`;
        const canonical = commonShorthand[entered] || entered;
        return {
          query: canonical,
          filters: { 'filter.p.m.custom.entraxe_roue': canonical },
        };
      }
    }

    match = compact.match(/^(?:(LT|P|ST))?(\d{3})\/(\d{2,3})[Rr](\d{2})(?:LT)?$/i);
    if (match) {
      if (+match[2] < 125 || +match[2] > 455 || +match[3] < 20 || +match[3] > 100 || +match[4] < 10 || +match[4] > 30) return null;
      const prefix = (match[1] || '').toUpperCase();
      const width = match[2];
      const profile = match[3];
      const rim = match[4];
      const canonical = `${prefix}${width}/${profile}R${rim}`;
      return {
        query: canonical,
        filters: {
          'filter.p.m.custom.largeur_pneu': width,
          'filter.p.m.custom.profil_pneu': profile,
          'filter.p.m.custom.diametre_pneu': rim,
        },
      };
    }

    match = compact.match(/^(?:LT)?(\d{2,3}(?:[.,]\d+)?)[xX×](\d{1,2}(?:[.,]\d+)?)[Rr-]?(\d{2})(?:LT)?$/i);
    if (match) {
      const overall = Number.parseFloat(match[1].replace(',', '.'));
      const width = Number.parseFloat(match[2].replace(',', '.'));
      const rim = Number.parseInt(match[3], 10);
      if (overall >= 25 && overall <= 60 && width >= 7 && width <= 20 && rim >= 10 && rim <= 30) {
        const queryOverall = match[1].replace(',', '.');
        const queryWidth = match[2].replace(',', '.');
        const canonicalOverall = normalizedNumber(match[1]);
        const canonicalWidth = normalizedNumber(match[2]);
        const canonical = `${queryOverall}x${queryWidth}R${rim}`;
        return {
          query: canonical,
          filters: {
            'filter.p.m.custom.diametre_exterieur_pneu': canonicalOverall,
            'filter.p.m.custom.largeur_pneu': canonicalWidth,
            'filter.p.m.custom.diametre_pneu': String(rim),
          },
        };
      }
    }

    return null;
  };
  const parseRequest = (value) => {
    const raw = stripQuotes(value);
    const whole = buildPreciseSearch(raw);
    if (whole) return { ...whole, textQuery: '*', displayQuery: whole.query };
    const candidates = Array.from(raw.matchAll(/(?:^|\s)((?:(?:LT|P|ST)?\d{3}[\/-]?\d{2}(?:[\/-]|Z?R)?\d{2}(?:LT)?|(?:LT)?\d{8}(?:LT)?|(?:LT)?\d{2}[x×]\d{1,2}(?:[.,]\d+)?[R-]?\d{2}(?:LT)?))(?=\s|$)/gi));
    const valid = candidates.map((m) => ({ m, size: buildPreciseSearch(m[1]) })).filter((x) => x.size);
    if (valid.length !== 1) return null;
    const { m, size } = valid[0];
    const rest = (raw.slice(0, m.index) + ' ' + raw.slice(m.index + m[0].length)).trim().replace(/\s+/g, ' ');
    return { ...size, textQuery: rest || '*', displayQuery: (rest + ' ' + size.query).trim() };
  };

  const isTireSize = (precise) => Object.hasOwn(precise.filters, 'filter.p.m.custom.largeur_pneu');
  const titleSizeQuery = (precise) => {
    if (!isTireSize(precise)) return precise.textQuery;
    const f = precise.filters;
    const width = f['filter.p.m.custom.largeur_pneu'];
    const rim = f['filter.p.m.custom.diametre_pneu'];
    const overall = f['filter.p.m.custom.diametre_exterieur_pneu'];
    const bases = new Set();
    if (overall) {
      for (const w of new Set([width, Number(width).toFixed(2)])) for (const x of ['x', '×']) bases.add(overall + x + w + 'R' + rim);
    } else {
      const profile = f['filter.p.m.custom.profil_pneu'];
      for (const radial of ['R', 'ZR']) bases.add(width + '/' + profile + radial + rim);
    }
    const aliases = new Set();
    for (const base of bases) for (const prefix of ['', 'LT', ...(overall ? [] : ['P', 'ST'])]) for (const suffix of ['', 'LT']) aliases.add(prefix + base + suffix);
    const terms = Array.from(aliases, (size) => 'title:"' + size + '"').join(' OR ');
    const rest = precise.textQuery === '*' ? '' : precise.textQuery.replace(/\b(pneus?|tires?)\b/gi, '').trim();
    return (rest ? '(' + rest + ') AND ' : '') + '(' + terms + ')';
  };
  const appendHiddenInput = (form, name, value) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    input.setAttribute(GENERATED_FILTER, '');
    form.appendChild(input);
  };

  // Do not show unrelated predictive suggestions for a recognized tire size.
  const sizeStyle = document.createElement('style');
  sizeStyle.textContent = '.jb-size-search [data-predictive-search]{display:none!important}.jb-size-search-hint{display:block;margin-top:8px}';
  document.head.appendChild(sizeStyle);
  document.addEventListener('input', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.name !== 'q') return;
    const form = input.closest('form');
    if (!form || form.id === 'FacetsFilterForm') return;
    const request = parseRequest(input.value);
    const tire = request && isTireSize(request);
    form.classList.toggle('jb-size-search', Boolean(tire));
    let hint = form.querySelector('.jb-size-search-hint');
    if (!tire) { if (hint) hint.remove(); return; }
    event.stopImmediatePropagation();
    input.setAttribute('aria-expanded', 'false');
    if (!hint) {
      hint = document.createElement('button');
      hint.type = 'submit';
      hint.className = 'button-text-link jb-size-search-hint';
      form.appendChild(hint);
    }
    hint.textContent = 'Voir les pneus : ' + request.displayQuery;
  }, true);
  const DIMENSION_PARAM = 'jb_dimension';
  const prepareForm = (form) => {
    if (form.id === 'FacetsFilterForm') return;
    const input = form.querySelector('input[name="q"]');
    if (!input) return;
    const precise = parseRequest(input.value);
    form.querySelectorAll('[' + GENERATED_FILTER + ']').forEach((element) => element.remove());
    if (!precise) return;
    // Numeric filters identify the size; title punctuation must never exclude it.
    input.value = titleSizeQuery(precise);
    appendHiddenInput(form, DIMENSION_PARAM, precise.displayQuery);
    if (!form.querySelector('input[name="type"]')) appendHiddenInput(form, 'type', 'product');
    if (isTireSize(precise)) appendHiddenInput(form, 'jb_size_mode', 'title');
    else Object.entries(precise.filters).forEach(([name, value]) => appendHiddenInput(form, name, value));
  };
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const action = new URL(form.action || window.location.href, window.location.origin);
    if (action.pathname === SEARCH_PATH) prepareForm(form);
  }, true);

  if (window.location.pathname === SEARCH_PATH) {
    const url = new URL(window.location.href);
    const currentQuery = url.searchParams.get('q') || '';
    const savedDimension = url.searchParams.get(DIMENSION_PARAM);
    const precise = parseRequest(savedDimension || currentQuery);
    if (precise) {
      const executableQuery = titleSizeQuery(precise);
      const migrateTire = isTireSize(precise) && url.searchParams.get('jb_size_mode') !== 'title';
      if (!savedDimension || migrateTire || (isTireSize(precise) && currentQuery !== executableQuery)) {
        url.searchParams.set('q', executableQuery);
        url.searchParams.set('type', 'product');
        url.searchParams.set(DIMENSION_PARAM, precise.displayQuery);
        if (isTireSize(precise)) {
          url.searchParams.set('jb_size_mode', 'title');
          if (migrateTire) Object.keys(precise.filters).forEach((name) => url.searchParams.delete(name));
        } else Object.entries(precise.filters).forEach(([name, value]) => url.searchParams.set(name, value));
        url.searchParams.delete('page');
        window.location.replace(url.toString());
        return;
      }
      const facets = document.getElementById('FacetsFilterForm');
      if (facets && !facets.querySelector('input[name="' + DIMENSION_PARAM + '"]')) appendHiddenInput(facets, DIMENSION_PARAM, precise.displayQuery);
      if (facets && isTireSize(precise) && !facets.querySelector('input[name="jb_size_mode"]')) appendHiddenInput(facets, 'jb_size_mode', 'title');
      document.querySelectorAll('input[name="q"]:not([type="hidden"])').forEach((input) => { input.value = precise.displayQuery; });
      const count = document.getElementById('ProductCount');
      if (count) count.textContent = count.textContent.replace('“' + currentQuery + '”', '“' + precise.displayQuery + '”');
      document.querySelectorAll('.page-hero-caption').forEach((caption) => {
        const walker = document.createTreeWalker(caption, NodeFilter.SHOW_TEXT);
        while (walker.nextNode()) walker.currentNode.textContent = walker.currentNode.textContent.replace(currentQuery, precise.displayQuery);
      });
      document.title = 'Recherche : ' + precise.displayQuery + ' – JB Performance';
      document.querySelectorAll('a[href]').forEach((link) => {
        const next = new URL(link.href, location.origin);
        if (next.origin !== location.origin || next.pathname !== SEARCH_PATH || next.searchParams.get('q') !== currentQuery) return;
        const sameSize = isTireSize(precise) || Object.entries(precise.filters).every(([name, value]) => next.searchParams.get(name) === value);
        if (sameSize) { if (isTireSize(precise)) next.searchParams.set('jb_size_mode', 'title'); next.searchParams.set(DIMENSION_PARAM, precise.displayQuery); link.href = next.toString(); }
      });
    }
  }
})();
